import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Plus, Search } from "lucide-react";
import { isEmpty } from "lodash-es";

import { AppStateContext } from "@/state/AppStateContext";
import { getAllAgents } from "@/api/api";
import { Agent } from "@/api/models";
import { doTrackEvent, newChat, setTitle, toggleHistoryPanel, useHandleTransitionEnd } from "@/utils/utils";
import AgentDetails from "@/components/Agents/AgentDetails";
import { Switch } from "@/components/ui/switch";

/**
 * Capitalize the first letter of the first two words of an agent name, used as its icon
 *
 * @param {string} inputString - The agent's name
**/
const lettersAsIcon = (inputString: string) => {
  const words = inputString.split(' ');
  const initials = [];

  if (words.length >= 1) initials.push(words[0].charAt(0).toUpperCase());
  if (words.length >= 2) initials.push(words[1].charAt(0).toUpperCase());

  return initials.join('');
}

/**
 * Truncate an agent description to a maximum length
 *
 * @param {string} description - The agent's description
 * @param {number} max - The maximum number of characters
**/
const truncate = (description: string, max: number) => (description.length > max ? description.substring(0, max) + '...' : description);

/**
 * Large card used to render an agent; the footer shows the creator
**/
const AgentCard = ({ agent, onOpen }: { agent: Agent, onOpen: (agent: Agent) => void }) => (
  <div className="flex items-stretch bg-base-100 hover:bg-base-200 border border-black/5 dark:border-white/5 rounded-3xl h-full shadow cursor-pointer gap-4 p-5" onClick={() => onOpen(agent)}>
    <div className="avatar static placeholder shrink-0">
      <div className="bg-neutral h-20 w-20 rounded-full">
        <span className="text-3xl">{lettersAsIcon(agent.name)}</span>
      </div>
    </div>
    <div className="flex flex-col flex-grow items-start text-start min-h-20 min-w-0">
      <h2 className="dark:text-white text-base font-bold truncate w-full mb-1">{agent.name}</h2>
      <p className="text-xs mb-3">{truncate(agent.description, 160)}</p>
      <span className="flex items-center text-xs text-black/60 dark:text-white/60 border-t border-black/10 dark:border-white/10 w-full mt-auto pt-2 gap-1">
        <span className="truncate">By: <strong>{agent.created_by_name}</strong></span>
      </span>
    </div>
  </div>
);

const AgentsHome = () => {
  const appStateContext = useContext(AppStateContext);
  const ENV = appStateContext?.state?.frontendSettings?.display_env || '';
  const CREATOR_GROUP = appStateContext?.state.frontendSettings?.creator_group || '';
  const navigate = useNavigate();
  const { agentId } = useParams();
  useHandleTransitionEnd();
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleToggleHistoryPanel = () => toggleHistoryPanel(appStateContext);
  const [viewing, setViewing] = useState<boolean>(false);
  const [hasRun, setHasRun] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [creator, setCreator] = useState<number>(0);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const isCreator = appStateContext?.state.userGroups.includes(CREATOR_GROUP) || ENV === 'DEV';

  /**
   * Handle checking for URL param to open a specific agent for editing
  **/
  useEffect(() => {
    if (!isEmpty(agentId)) setViewing(true);
  }, [agentId, appStateContext?.state.agents]);

  /**
   * Track initial load of page
  **/
  useEffect(() => {
    setTitle(appStateContext, 'Agents');
    newChat(appStateContext);
    appStateContext?.dispatch({ type: 'SET_CURRENT_CUSTOM_GPT', payload: null });
    setHasRun(true);
  }, []);

  /**
   * Handle reloading Agents when toggle changes
  **/
  useEffect(() => {
    loadAgents();
  }, [viewing, creator]);

  /**
   * Debounce search input (delay rate at which event fires and performs API call to query Agents)
  **/
  useEffect(() => {
    if (hasRun) {
      const h = setTimeout(() => { loadAgents(); }, 300);
      return () => clearTimeout(h);
    }
  }, [searchText]);

  /**
   * Handle loading all Agents
  **/
  const loadAgents = async () => {
    // Toggle loading state
    setLoading(true);

    // Get data
    const res = await getAllAgents(searchText, creator);

    // Set state
    setAgents(res);

    // Toggle loading state
    setLoading(false);
  }

  /**
   * Handle opening a specific agent
   *
   * @param {Agent} agent - The agent to open
  **/
  const openAgent = async (agent: Agent) => {
    setSelectedAgent(null);
    setTimeout(() => {
      setSelectedAgent(agent);
      setIsViewDialogOpen(true);
    }, 100);
  }

  return (
    <div className={`content flex flex-col h-full ${appStateContext?.state.sidepanelOpen ? 'shifted-left' : ''}`}>
      {!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning &&
        <div className="flex justify-between items-center mx-2">
          <button type="button" className="btn btn-ghost px-2" onClick={handleToggleHistoryPanel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-xl-heavy"><path fillRule="evenodd" clipRule="evenodd" d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118ZM11.5 5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V10.4C20.5 9.26339 20.4992 8.47108 20.4488 7.85424C20.3994 7.24907 20.3072 6.90138 20.173 6.63803C19.8854 6.07354 19.4265 5.6146 18.862 5.32698C18.5986 5.19279 18.2509 5.10062 17.6458 5.05118C17.0289 5.00078 16.2366 5 15.1 5H11.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z" fill="currentColor"></path></svg>
          </button>

          {isCreator ?
            <button type="button" className="btn btn-sm btn-primary shrink-0 pe-4" onClick={() => navigate('/agents/c')}><Plus className="mt-0.5" size={20} /> Create</button>
          : null}
        </div>
      }

      <div className="flex flex-grow justify-center h-[90vh]">
        <div className="w-full mx-auto">
          <div className="main-div flex flex-col flex-grow items-center justify-center !h-full transitioned">
            <div className="child-div flex flex-col w-full gg">
              <div className="child-div-parent flex flex-col flex-grow w-full items-center scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent pb-2">
                <div className="flex flex-col w-full text-start px-6">
                  <div className={`flex items-start justify-between gap-4 ${((!appStateContext?.state.sidepanelOpen && !appStateContext?.state.sidepanelTransitioning) ? '' : 'mt-4')}`}>
                    <div className="flex gap-4">
                      <div>
                        <h1 className="dark:text-white text-3xl font-bold">Agents</h1>
                        {!window.matchMedia('(max-width: 700px)').matches ?
                          <>
                            <h4 className="dark:text-gray-400 text-sm mt-4">Discover and create custom Agents that combine Spaces with agentic actions.</h4>
                            <h4 className="dark:text-gray-400 text-sm mt-2">If you have an idea for a new agent, please reach out to your <a className="text-primary underline" href="https://smsgroupgmbh.sharepoint.com/sites/intranet-content/SitePages/AI-Accelerators-and-Key-Users.aspx?xsdata=MDV8MDJ8fDdlNDA0ZmJmZWI5ZDRlZmNjNWExMDhkZGMyZDIzOWVhfDA2OWRiOTE0YzU0ODQ4OGZiOWNiYjUzNDA1YTA1ZDQ3fDB8MHw2Mzg4ODA5MzAzMjcyNDI3ODl8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T2pCbE1qRmxNV1ZsTFRNeU9URXROR1E1WVMwNFptTXdMV1ppWVRaaU4yVmpOVGhrTWw4ek1XTmxOV1E1WXkxa1lXUTVMVFF3WmpZdE9EZ3hOeTAzWWpKaE1XRTFOekF5Wm1aQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjMU1qUTVOakl6TVRZNE5BPT18MTkyYjJmMDhkN2E2NGRjM2M1YTEwOGRkYzJkMjM5ZWF8OWE4OGZlNzFjOTc4NDk5MDgzYTg5OGQ3MzFlY2Q2ZmQ%3D&sdata=bUNyYnVqZFErQzlnUUxTYzdzNzVKQW00V2VXNlNUNlBCbEVxUG5tbXk4UT0%3D&ovuser=069db914-c548-488f-b9cb-b53405a05d47%2Cej.cox%40sms-group.com&OR=Teams-HL&CT=1752496272768&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI0OS8yNTA2MTIxNjQyNCIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D" target="_blank">AI Accelerator</a>.</h4>
                          </>
                        : null}
                      </div>
                    </div>

                    {(appStateContext?.state.sidepanelOpen || appStateContext?.state.sidepanelTransitioning) && isCreator &&
                      <button type="button" className="btn btn-sm btn-primary shrink-0 pe-4" onClick={() => navigate('/agents/c')}><Plus className="mt-0.5" size={20} /> Create</button>
                    }
                  </div>

                  {/* Search */}
                  <div className="w-full mt-6">
                    <label className="input bg-neutral border-accent border-opacity-50 focus-within:border-accent focus-within:border-opacity-50 focus-within:outline-none flex items-center gap-2">
                      <Search size={16} className="opacity-70 z-0" />
                      <input type="text" className="grow" placeholder="Search Agents..." autoComplete="off" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </label>
                  </div>

                  {/* Toolbar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                    {isCreator ?
                      <div className="flex relative bg-secondary rounded-md p-1">
                        <Switch className="dark:text-white text-sm w-[75px] p-2" checked={creator == 0} onCheckedChange={() => setCreator(0)}>All</Switch>
                        <Switch className="dark:text-white text-sm w-[75px] p-2" checked={creator == 1} onCheckedChange={() => setCreator(1)}>Mine</Switch>
                      </div>
                    : <span />}

                    <button type="button" className="btn btn-sm btn-ghost text-primary" onClick={() => { appStateContext?.dispatch({ type: 'SET_SELECTED_TOOL', payload: 'cgpt_selector' }); navigate('/'); handleTrackEvent('click', 'try_custom_gpt_selector_agents', 'Try Custom GPT Selector (Agents)', '', '', ''); }}>
                      Try the Spaces Selector Agent
                      <ArrowRight size={16} />
                    </button>
                  </div>

                  {loading ?
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-6 mt-6">
                      {Array.from({ length: 9 }, (_, index) => index).map((x, idx) =>
                        <div key={idx} className="flex items-stretch bg-base-100 border border-black/5 dark:border-white/5 rounded-3xl gap-4 p-5">
                          <div className="skeleton h-20 w-20 shrink-0 rounded-full"></div>
                          <div className="flex flex-col flex-grow min-h-20 min-w-0">
                            <div className="skeleton h-4 w-2/3 mb-2"></div>
                            <div className="skeleton h-3 w-full mb-1"></div>
                            <div className="skeleton h-3 w-4/5 mb-3"></div>
                            <div className="skeleton h-3 w-1/3 mt-auto"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  : agents.length === 0 ?
                    <div className="text-sm text-black/60 dark:text-white/60 mt-6">No Agents found.</div>
                  :
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 w-full gap-6 mt-6">
                      {agents.map((agent: Agent, idx: number) => <AgentCard key={agent.id || idx} agent={agent} onOpen={openAgent} />)}
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AgentDetails isViewDialogOpen={isViewDialogOpen} setIsViewDialogOpen={setIsViewDialogOpen} selectedAgent={selectedAgent} editable={true} />
    </div>
  );
}

export default AgentsHome;
